// ---------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------
// Cognito, over SRP. The password is exchanged with the user pool directly and
// never reaches the support API — which is why there are no demo accounts
// listed here any more: there is no password this application knows.
//
// The pool requires MFA, so a first sign-in walks three steps rather than one:
// set a real password, enrol an authenticator, then prove it. Each is its own
// screen with its own instruction, because "enter the code" is not a useful
// prompt when the reader has not yet been told to add an account to an app.
//
// Forgetting the password is the fourth path, and it is self-service for the
// same reason the others are: the pool recovers through the verified email
// address, so nothing here needs an administrator's working hours. A reset
// proves one factor only — the authenticator is still asked for afterwards.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  KeyRound,
  LifeBuoy,
  LogIn,
  Mail,
  MailCheck,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import TotpQrCode from '../components/TotpQrCode';
import { useAuth } from '../auth/AuthContext';
import { isCognitoConfigured } from '../auth/cognito';
import { Button, Field, Input } from '../ui/Primitives';
import './SignIn.css';

/**
 * Cognito's reset failures, in words that say what to do next.
 *
 * Two of these are not failures in the reader's sense. An account still on its
 * invitation has no password to reset — Cognito calls that "User password
 * cannot be reset in the current state", which tells the reader nothing and
 * sends them to an administrator who cannot help either. An expired code just
 * needs asking again.
 *
 * Anything unmapped keeps Cognito's own message: a wrong guess dressed up in
 * confident prose is worse than an awkward sentence that is true.
 */
function resetErrorMessage(err) {
  switch (err?.name) {
    case 'NotAuthorizedException':
      return (
        'This account has never been signed in to, so there is no password to reset yet. ' +
        'Use the temporary password from your invitation email, or ask an administrator to send a new invitation.'
      );
    case 'CodeMismatchException':
      return 'That code is not right. Check the email and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one.';
    case 'InvalidPasswordException':
      return 'That password does not meet the policy: at least 12 characters, with upper and lower case, a number and a symbol.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'UserNotFoundException':
      // Only reachable if prevent_user_existence_errors is ever turned off on
      // the app client. Answer as though it were still on.
      return 'If an account exists for that address, a code has been sent to it.';
    default:
      return null;
  }
}

export default function SignIn() {
  const { signIn, respondToChallenge, requestPasswordReset, confirmPasswordReset, error: sessionError } =
    useAuth();

  const [view, setView] = useState('credentials'); // credentials | reset-request | reset-confirm
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetDestination, setResetDestination] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  /** Run a sign-in step and adopt whatever Cognito asks for next. */
  async function attempt(step) {
    setBusy(true);
    setError(null);
    try {
      const { done, challenge: next } = await step();
      if (!done) {
        setChallenge(next);
        setAnswer('');
        setConfirmPassword('');
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  /** Run a reset step, translating Cognito's vocabulary on the way out. */
  async function attemptReset(step) {
    setBusy(true);
    setError(null);
    try {
      await step();
    } catch (err) {
      const friendly = resetErrorMessage(err);
      setError(friendly ? new Error(friendly) : err);
    } finally {
      setBusy(false);
    }
  }

  const submitCredentials = (e) => {
    e?.preventDefault();
    setNotice(null);
    return attempt(() => signIn(email.trim(), password));
  };

  const submitAnswer = (e) => {
    e?.preventDefault();
    return attempt(() => respondToChallenge(answer.trim()));
  };

  const submitNewPassword = (e) => {
    e?.preventDefault();
    if (answer !== confirmPassword) {
      setError(new Error('The two passwords do not match.'));
      return undefined;
    }
    return attempt(() => respondToChallenge(answer));
  };

  /** Leave whatever flow is in progress and return to the credentials form. */
  function backToSignIn(message = null) {
    setView('credentials');
    setChallenge(null);
    setAnswer('');
    setConfirmPassword('');
    setResetCode('');
    setResetDestination(null);
    setPassword('');
    setError(null);
    setNotice(message);
  }

  function beginReset() {
    setView('reset-request');
    setChallenge(null);
    setAnswer('');
    setConfirmPassword('');
    setResetCode('');
    setError(null);
    setNotice(null);
  }

  const submitResetRequest = (e) => {
    e?.preventDefault();
    return attemptReset(async () => {
      const { done, destination } = await requestPasswordReset(email.trim());
      if (done) {
        // Cognito wants no code — nothing is left to confirm, so say so
        // rather than showing a form with nothing to put in it. Without a
        // verified code, the old authenticator must not be cleared.
        backToSignIn(
          'Cognito completed recovery without a verification code, so your authenticator was not reset. Contact support for help.',
        );
        return;
      }
      setChallenge(null);
      setResetDestination(destination);
      setResetCode('');
      setAnswer('');
      setConfirmPassword('');
      setView('reset-confirm');
    });
  };

  const submitResetConfirm = (e) => {
    e?.preventDefault();
    if (answer !== confirmPassword) {
      setError(new Error('The two passwords do not match.'));
      return undefined;
    }
    return attemptReset(async () => {
      await confirmPasswordReset({
        email: email.trim(),
        code: resetCode.trim(),
        newPassword: answer,
      });
      backToSignIn(
        'Your password and authenticator setup have been reset. Sign in with your new password to enroll a new authenticator.',
      );
    });
  };

  const onCredentials = view === 'credentials' && !challenge;

  return (
    <div className="si">
      <div className="si-panel">
        <div className="si-brand">
          <LifeBuoy size={22} className="si-mark" />
          <div>
            <div className="si-title">CliniqVoice Support</div>
            <div className="si-sub">Customer portal and support operations</div>
          </div>
        </div>

        {!isCognitoConfigured ? (
          <Notice tone="error">
            This build has no identity pool configured, so nobody can sign in. Set
            VITE_SUPPORT_USER_POOL_ID and VITE_SUPPORT_CLIENT_ID and rebuild.
          </Notice>
        ) : (
          <>
            {/* An account that authenticates but holds no support role lands
                here with the explanation, rather than at a blank console. */}
            {onCredentials && sessionError?.status === 403 && (
              <Notice tone="warn">{sessionError.message}</Notice>
            )}

            {onCredentials && notice && (
              <Notice tone="success" icon={CheckCircle2}>
                {notice}
              </Notice>
            )}

            {onCredentials && (
              <form onSubmit={submitCredentials} className="si-form">
                <Field label="Email" htmlFor="si-email">
                  <Input
                    id="si-email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@cliniqvoice.com"
                    required
                    autoFocus
                  />
                </Field>

                <Field label="Password" htmlFor="si-password">
                  <Input
                    id="si-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </Field>

                <ErrorLine error={error} />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  icon={LogIn}
                  loading={busy}
                  disabled={!email || !password}
                  style={{ width: '100%' }}
                >
                  Sign in
                </Button>

                <button type="button" className="si-link" onClick={beginReset}>
                  Forgot your password?
                </button>
              </form>
            )}

            {challenge?.kind === 'new-password' && (
              <form onSubmit={submitNewPassword} className="si-form">
                <Notice tone="info" icon={KeyRound}>
                  This account is still on the temporary password it was created with. Choose one
                  of your own to continue.
                </Notice>

                <Field
                  label="New password"
                  hint="At least 12 characters, with upper and lower case, a number and a symbol."
                  htmlFor="si-new"
                >
                  <Input
                    id="si-new"
                    type="password"
                    autoComplete="new-password"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    required
                    autoFocus
                  />
                </Field>

                <Field
                  label="Confirm new password"
                  htmlFor="si-confirm"
                  error={
                    confirmPassword && confirmPassword !== answer ? 'These do not match.' : undefined
                  }
                >
                  <Input
                    id="si-confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </Field>

                <ErrorLine error={error} />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={busy}
                  disabled={answer.length < 12 || answer !== confirmPassword}
                  style={{ width: '100%' }}
                >
                  Set password and continue
                </Button>
                <StartOver onClick={() => backToSignIn()} />
              </form>
            )}

            {challenge?.kind === 'totp-setup' && (
              <form onSubmit={submitAnswer} className="si-form">
                <Notice tone="info" icon={ShieldCheck}>
                  These accounts can read every customer's support tickets, so they need a second
                  factor. Scan this with an authenticator app, then enter the code it shows.
                </Notice>

                {challenge.uri && (
                  <Field
                    label="Scan to enrol"
                    hint="Open your authenticator app and point it at this code."
                  >
                    <TotpQrCode uri={challenge.uri} />
                  </Field>
                )}

                {/* Kept alongside the QR, not replaced by it. Plenty of people
                    enrol from a desktop authenticator with no camera, and a
                    code that will not scan on a dim screen is common enough
                    that removing the typed path would strand them. */}
                {challenge.secret && (
                  <Field
                    label="Setup key"
                    hint={challenge.uri
                      ? "Or enter this by hand if you can't scan."
                      : 'Enter this in your authenticator app.'}
                  >
                    <div className="si-secret mono">{challenge.secret}</div>
                  </Field>
                )}

                <Field label="Code from your authenticator" htmlFor="si-totp-setup">
                  <Input
                    id="si-totp-setup"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="123456"
                    required
                    autoFocus
                  />
                </Field>

                <ErrorLine error={error} />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={busy}
                  disabled={!answer}
                  style={{ width: '100%' }}
                >
                  Confirm authenticator
                </Button>
                <StartOver onClick={() => backToSignIn()} />
              </form>
            )}

            {challenge?.kind === 'totp' && (
              <form onSubmit={submitAnswer} className="si-form">
                <Notice tone="info" icon={Smartphone}>
                  Enter the six-digit code from your authenticator app.
                </Notice>

                <Field label="Authentication code" htmlFor="si-totp">
                  <Input
                    id="si-totp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="123456"
                    required
                    autoFocus
                  />
                </Field>

                <ErrorLine error={error} />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={busy}
                  disabled={!answer}
                  style={{ width: '100%' }}
                >
                  Verify
                </Button>
                <StartOver onClick={() => backToSignIn()} />
              </form>
            )}

            {/* Cognito has flagged the account for reset. This used to be a
                dead end pointing at an administrator; the pool recovers by
                email, so the reader can simply do it. */}
            {challenge?.kind === 'reset-required' && (
              <form onSubmit={submitResetRequest} className="si-form">
                <Notice tone="warn">
                  This account's password has to be reset before it can be used. We can email a
                  code to {email.trim() || 'the address on the account'} to reset the password
                  and authenticator setup.
                </Notice>

                <ErrorLine error={error} />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  icon={Mail}
                  loading={busy}
                  style={{ width: '100%' }}
                >
                  Email me a reset code
                </Button>
                <StartOver onClick={() => backToSignIn()} />
              </form>
            )}

            {challenge?.kind === 'unsupported' && (
              <>
                <Notice tone="error">
                  Your account needs a sign-in step this console does not handle
                  ({challenge.step}). Please tell an administrator.
                </Notice>
                <StartOver onClick={() => backToSignIn()} />
              </>
            )}

            {view === 'reset-request' && !challenge && (
              <form onSubmit={submitResetRequest} className="si-form">
                <Notice tone="info" icon={Mail}>
                  Enter your address and we will email a code to reset your password and
                  authenticator setup. You will enroll a new authenticator when you sign in again.
                </Notice>

                <Field label="Email" htmlFor="si-reset-email">
                  <Input
                    id="si-reset-email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@cliniqvoice.com"
                    required
                    autoFocus
                  />
                </Field>

                <ErrorLine error={error} />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  icon={Mail}
                  loading={busy}
                  disabled={!email.trim()}
                  style={{ width: '100%' }}
                >
                  Send reset code
                </Button>
                <StartOver onClick={() => backToSignIn()} label="Back to sign in" />
              </form>
            )}

            {view === 'reset-confirm' && !challenge && (
              <form onSubmit={submitResetConfirm} className="si-form">
                {/* Deliberately conditional, and deliberately "if an account
                    exists": the app client hides whether an address is
                    registered, and this sentence must not give it away. */}
                <Notice tone="info" icon={MailCheck}>
                  If an account exists for {email.trim()}, a code is on its way
                  {resetDestination ? ` to ${resetDestination}` : ''}. Enter it below with the
                  password you want.
                </Notice>

                <Field label="Reset code" htmlFor="si-reset-code">
                  <Input
                    id="si-reset-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    placeholder="123456"
                    required
                    autoFocus
                  />
                </Field>

                <Field
                  label="New password"
                  hint="At least 12 characters, with upper and lower case, a number and a symbol."
                  htmlFor="si-reset-new"
                >
                  <Input
                    id="si-reset-new"
                    type="password"
                    autoComplete="new-password"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    required
                  />
                </Field>

                <Field
                  label="Confirm new password"
                  htmlFor="si-reset-confirm"
                  error={
                    confirmPassword && confirmPassword !== answer ? 'These do not match.' : undefined
                  }
                >
                  <Input
                    id="si-reset-confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </Field>

                <ErrorLine error={error} />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  icon={KeyRound}
                  loading={busy}
                  disabled={!resetCode.trim() || answer.length < 12 || answer !== confirmPassword}
                  style={{ width: '100%' }}
                >
                  Set new password
                </Button>

                <button
                  type="button"
                  className="si-link"
                  onClick={submitResetRequest}
                  disabled={busy}
                >
                  Send another code
                </button>
                <StartOver onClick={() => backToSignIn()} label="Back to sign in" />
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Notice({ tone = 'info', icon: Icon = AlertCircle, children }) {
  return (
    <div className={`si-note si-note-${tone}`} role={tone === 'info' ? undefined : 'alert'}>
      <Icon size={14} />
      <div>{children}</div>
    </div>
  );
}

function ErrorLine({ error }) {
  if (!error) return null;
  return (
    <div className="si-note si-note-error" role="alert">
      <AlertCircle size={14} />
      <div>
        <div>{error.message}</div>
        {error.incidentId && <div className="xs mono subtle">Incident {error.incidentId}</div>}
      </div>
    </div>
  );
}

function StartOver({ onClick, label = 'Start again' }) {
  return (
    <Button variant="ghost" size="sm" onClick={onClick} type="button" style={{ width: '100%' }}>
      {label}
    </Button>
  );
}
